import { FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { faThumbsDown, faThumbsUp } from "@fortawesome/pro-regular-svg-icons";

import { HelpChatFeedback } from "@signalist/shared-types";

import { classNames } from "@/utils/classNames";
import { Icon } from "@/components/Icon/Icon";
import { PageHeader } from "@/components/PageHeader/PageHeader";
import { SearchField } from "@/components/SearchField/SearchField";
import { Table, TableColumn } from "@/components/Table/Table";

import { ROUTE_CONFIG } from "@/routes/config";
import { useGetHelpConversationsQuery } from "@/pages/Private/redux/helpCenter/helpConversations.api";
import { HelpConversationSummary } from "@/pages/Private/redux/helpCenter/helpConversations.schema";

const ta = "helpCenter.admin.conversations";
const PAGE_SIZE = 20;

/** Compact date-time for the review list/detail. */
const formatDateTime = (iso: string): string => new Date(iso).toLocaleString();

/** Thumbs tally with the down-count emphasised (weak answers are what support hunts for). */
const FeedbackTally: FunctionComponent<{ up: number; down: number }> = ({ up, down }) => {
	if (up === 0 && down === 0) {
		return <span className="text-xs text-text-disabled">—</span>;
	}

	return (
		<div className="flex items-center gap-2">
			{up > 0 && (
				<span className="flex items-center gap-1 text-xs text-status-success">
					<Icon icon={faThumbsUp} />
					{up}
				</span>
			)}
			{down > 0 && (
				<span className="flex items-center gap-1 text-xs font-semibold text-status-error">
					<Icon icon={faThumbsDown} />
					{down}
				</span>
			)}
		</div>
	);
};

/**
 * Support-review list of saved AI help conversations (GLOBAL_ADMIN). Filter by text and by
 * feedback to surface weak answers, then open a transcript to see what went wrong and improve
 * the underlying article.
 */
export const AdminConversationList: FunctionComponent = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [page, setPage] = useState(1);
	const [searchValue, setSearchValue] = useState("");
	const [feedback, setFeedback] = useState<HelpChatFeedback | undefined>(undefined);

	const { data, isLoading, isFetching } = useGetHelpConversationsQuery({
		page,
		limit: PAGE_SIZE,
		searchValue: searchValue || undefined,
		feedback,
	});

	const conversations = data?.data ?? [];

	// A filter change resets to the first page so the user never lands on an out-of-range page.
	const applySearch = (value: string) => {
		setSearchValue(value);
		setPage(1);
	};

	const applyFeedbackFilter = (value: HelpChatFeedback | undefined) => {
		setFeedback(value);
		setPage(1);
	};

	const feedbackFilters: { label: string; value: HelpChatFeedback | undefined }[] = [
		{ label: t(`${ta}.filterAll`), value: undefined },
		{ label: t(`${ta}.filterDown`), value: HelpChatFeedback.DOWN },
		{ label: t(`${ta}.filterUp`), value: HelpChatFeedback.UP },
	];

	const columns: TableColumn[] = useMemo(
		() => [
			{
				title: t(`${ta}.columns.question`),
				render: (row: HelpConversationSummary) => (
					<span className="line-clamp-2 text-sm font-medium text-text">
						{row.firstQuestion ?? "—"}
					</span>
				),
			},
			{
				title: t(`${ta}.columns.language`),
				width: 90,
				render: (row: HelpConversationSummary) => (
					<span className="text-xs uppercase text-text-meta">{row.language ?? "—"}</span>
				),
			},
			{
				title: t(`${ta}.columns.turns`),
				width: 80,
				render: (row: HelpConversationSummary) => (
					<span className="text-sm text-text-meta">{row.messageCount}</span>
				),
			},
			{
				title: t(`${ta}.columns.feedback`),
				width: 110,
				render: (row: HelpConversationSummary) => (
					<FeedbackTally down={row.downCount} up={row.upCount} />
				),
			},
			{
				title: t(`${ta}.columns.company`),
				width: 90,
				render: (row: HelpConversationSummary) => (
					<span className="text-xs text-text-meta">{row.companyId ?? "—"}</span>
				),
			},
			{
				title: t(`${ta}.columns.date`),
				width: 170,
				render: (row: HelpConversationSummary) => (
					<span className="text-xs text-text-meta">{formatDateTime(row.createdAt)}</span>
				),
			},
		],
		[t]
	);

	return (
		<div>
			<PageHeader title={t(`${ta}.title`)} />

			<div className="mb-3 flex flex-wrap items-center gap-2">
				<SearchField
					handleChange={(event) => applySearch(event.target.value)}
					handleReset={() => applySearch("")}
					name="help-conversation-search"
					placeholder={t(`${ta}.searchPlaceholder`)}
					value={searchValue}
				/>
				<div className="flex items-center gap-1.5">
					{feedbackFilters.map((filter) => (
						<button
							className={classNames(
								"cursor-pointer rounded-[10px] border px-3 text-xs font-semibold duration-200 h-[32px]",
								feedback === filter.value
									? "border-brand-light text-brand-light"
									: "border-gray-200 text-text-meta hover:border-brand-light"
							)}
							key={filter.label}
							onClick={() => applyFeedbackFilter(filter.value)}
						>
							{filter.label}
						</button>
					))}
				</div>
			</div>

			<Table
				columns={columns}
				data={conversations}
				handleFetchPage={(nextPage) => setPage(nextPage)}
				handleRowClick={(id) => navigate(`${ROUTE_CONFIG.HELP_CENTER_ADMIN}/${id}`)}
				hidePagination={false}
				isFetching={isFetching}
				isLoading={isLoading && conversations.length === 0}
				limit={PAGE_SIZE}
				noDataText={t(`${ta}.empty`)}
				overallCount={data?.meta.itemCount ?? 0}
				page={page}
				totalPages={data?.meta.pageCount ?? 0}
			/>
		</div>
	);
};
